import { addGlobalExtras } from './loader-component';
const MFEParentNode = Symbol('mfe-parent-node');
/**
 * Function to provide all extras required by Angular for {@link MFELoader}.
 * @public
 * @param extras - Object with the extras.
 */
export function ngAddGlobalExtras(extras) {
    addGlobalExtras(extras);
}
function assertBootstrappedState(state) {
    if (!state.ngModuleRef) {
        /* C8 ignore next */
        throw new Error(`mfe-loader: the 'ngModuleRef' instance is not ready. Make sure of call 'bootstrap' before`);
    }
}
function assertMountedState(state) {
    assertBootstrappedState(state);
    if (!state.changeDetectionRef) {
        /* C8 ignore next */
        throw new Error(`mfe-loader: the 'changeDetectionRef' instance is not ready. Make sure of call 'mount' before`);
    }
    if (!state.componentFactory) {
        /* C8 ignore next */
        throw new Error(`mfe-loader: the 'componentFactory' instance is not ready. Make sure of call 'mount' before`);
    }
    if (!state.componentInjector) {
        /* C8 ignore next */
        throw new Error(`mfe-loader: the 'componentInjector' instance is not ready. Make sure of call 'mount' before`);
    }
    if (!state.componentRef) {
        /* C8 ignore next */
        throw new Error(`mfe-loader: the 'componentRef' instance is not ready. Make sure of call 'mount' before`);
    }
}
/**
 * This map keeps track of each Angular Module instance, we should have only one copy of each.
 * @remarks This must be pruned on unmount to avoid memory leaks.
 */
const modulesCache = new Map();
/**
 * Keeps track of the number of usage of each Angular module.
 * @remarks Being a WeakMap save as the need to pruning it.
 */
const moduleUsages = new WeakMap();
/**
 * Keeps of if a module has their Router patched with Zone change detection.
 * @remarks Being a WeakMap save as the need to pruning it.
 */
const routerPatchedModules = new WeakSet();
/**
 * Bootstraps the MFE by creation the module and updating the state with it and the Zone object.
 * @param state - {@link State} instance to update.
 * @param options - {@link MFELoaderOptions} Options object that provides the Angular Platform and the Angular Modules.
 */
async function bootstrap(state, options) {
    var _a, _b;
    const ngPlatform = (_a = options.ngPlatform /* c8 ignore next */) !== null && _a !== void 0 ? _a : (await import('@angular/platform-browser-dynamic')).platformBrowserDynamic();
    const compilerOptions = {
        ngZone: options.useZone /* c8 ignore next */ ? 'zone.js' : 'noop',
    };
    const ngModuleRefPromise = (_b = modulesCache.get(options.ngModule)) !== null && _b !== void 0 ? _b : ngPlatform.bootstrapModule(options.ngModule, compilerOptions);
    modulesCache.set(options.ngModule, ngModuleRefPromise);
    state.ngModuleRef = await ngModuleRefPromise;
    // When the module gets deleted, remove it from the cache
    state.ngModuleRef.onDestroy(() => {
        modulesCache.delete(options.ngModule);
    });
    assertBootstrappedState(state);
    if (options.useZone) {
        const { NgZone } = await import('@angular/core');
        state.ngZone = state.ngModuleRef.injector.get(NgZone);
    }
    await addRouterPatches(state);
}
/**
 * This method parse an Angular Component selector to try to get a valid element selector.
 * @param selector - Angular Component selector.
 * @returns DOM tag name.
 */
function getDomElementTag(selector) {
    // If the selector is not an element selector, create a ng-component (emulates Angular behavior about dynamic components)
    if (!selector || ['.', '[', ':'].includes(selector[0])) {
        return 'ng-component';
    }
    // If the component has multiple selectors, pick the fist one
    return selector.split(',')[0];
}
/**
 * Changes a tracker Map or WeakMap.
 * @param map - Map to change.
 * @param key - Key used in the map.
 * @param change - Number to change.
 */
function updateTracker(map, key, change) {
    var _a;
    let moduleUsage = (_a = map.get(key)) !== null && _a !== void 0 ? _a : 0;
    moduleUsage += change;
    map.set(key, moduleUsage);
    return moduleUsage;
}
/**
 * Creates the Angular component and mount it on the mount point.
 * @param state - State instance to update.
 * @param options - Options object that provides the Angular Platform and the Angular Modules.
 * @param mountPoint - DOM element where the component will get mounted.
 * @param props - Optional properties that will be propagated as Inputs changes.
 */
async function mount(state, options, mountPoint, props) {
    assertBootstrappedState(state);
    const parent = mountPoint();
    const { Injector, ChangeDetectorRef, ApplicationRef } = await import('@angular/core');
    // Increment the usage tracker by one
    updateTracker(moduleUsages, state.ngModuleRef, 1);
    state.componentInjector = Injector.create({
        providers: [{ provide: MFEParentNode, useValue: parent }],
        parent: state.ngModuleRef.injector,
    });
    state.componentFactory = state.ngModuleRef.componentFactoryResolver.resolveComponentFactory(options.ngModule.initComponent);
    // Create a new DOM element to be the mount point of the new component
    // Try to infer the selector from the real component, but if not possible use a fallback
    const tag = getDomElementTag(state.componentFactory.selector);
    const dom = document.createElement(tag);
    parent.prepend(dom);
    state.inputRecords = state.componentFactory.inputs.map(i => i.propName);
    state.componentRef = state.componentFactory.create(state.componentInjector, [], dom);
    state.changeDetectionRef = state.componentRef.injector.get(ChangeDetectorRef);
    // Make sure that all Angular parts are properly created this far
    assertMountedState(state);
    // Propagates the first batch of changes along with the ngOnInit
    await extractChangesFromProps(state, props /* c8 ignore next */ !== null && props /* c8 ignore next */ !== void 0 ? props /* c8 ignore next */ : {}, true);
    triggerChangeDetection(state);
    // Attache the component to the application view so it's part of the update cycles
    const applicationRef = state.ngModuleRef.injector.get(ApplicationRef);
    applicationRef.attachView(state.componentRef.hostView);
    // When Zone less mode is used, the app needs a little kick to get the first render in place
    if (!options.useZone) {
        applicationRef.tick();
    }
}
/**
 * Propagates and Input changes and trigger change detection in Component tree.
 * @param state - State instance to update.
 */
function triggerChangeDetection(state) {
    // Update the instance of the component before anything
    for (const name in state.inputChanges) {
        if (Object.prototype.hasOwnProperty.call(state.inputChanges, name)) {
            state.componentRef.instance[name] = state.inputChanges[name].currentValue;
        }
    }
    // Only if the component implements OnChanges, calls it
    if ('ngOnChanges' in state.componentRef.instance) {
        state.componentRef.instance.ngOnChanges(state.inputChanges);
    }
    // Reset the changes
    state.inputChanges = {};
    // Mark the app for updates and kicks a re-render
    state.changeDetectionRef.markForCheck();
    state.componentRef.changeDetectorRef.detectChanges();
}
/**
 * Unmounts a component from the Application, and dispose all objects and resources.
 * @param state - State instance to unmount and clean.
 */
async function unmount(state) {
    var _a;
    // This is done this way to remove the memory reference from the state to this Angular Module
    // But this module could live longer is still in sure
    // Doing this we ensure that when the last user is unmounted, the memory gets properly cleared
    const moduleRef = state.ngModuleRef;
    state.ngModuleRef = undefined;
    (_a = state.componentRef) === null || _a === void 0 ? void 0 : _a.destroy();
    state.ngZone = undefined;
    state.componentRef = undefined;
    state.componentInjector = undefined;
    state.componentFactory = undefined;
    state.componentRef = undefined;
    state.changeDetectionRef = undefined;
    if (moduleRef) {
        // Only destroy the module is this was the last user of it
        const pendingUsages = updateTracker(moduleUsages, moduleRef, -1);
        if (pendingUsages < 1) {
            moduleRef.destroy();
        }
    }
}
/**
 * Transform a simple object with properties to an Angular SimpleChanges object.
 * @remarks The state `inputChanges` will be updated with the latest values.
 * @param state - State used to keep track of the last input values.
 * @param props - New values to transform.
 * @param isFistChange - Defines if this is the fist change during component creation or not.
 * @returns SimpleChanges instance.
 */
async function extractChangesFromProps(state, props, isFistChange) {
    const { SimpleChange } = await import('@angular/core');
    const changes = {};
    for (const name of state.inputRecords) {
        if (isFistChange && name in state.componentRef.instance) {
            changes[name] = new SimpleChange(undefined, state.componentRef.instance[name], isFistChange);
        }
        if (name in props) {
            changes[name] = new SimpleChange(state.componentRef.instance[name], props[name], isFistChange);
        }
    }
    state.inputChanges = changes;
}
/**
 * Propagates changes into the Angular Component.
 * @param state - State object.
 * @param props - Properties changes.
 */
async function update(state, props) {
    assertMountedState(state);
    await extractChangesFromProps(state, props /* c8 ignore next */ !== null && props /* c8 ignore next */ !== void 0 ? props /* c8 ignore next */ : {}, false);
    triggerChangeDetection(state);
}
/**
 * Patches the Router in the Angular Module tree to be Zone aware even when the Route changes are not handled by Angular.
 * @param state - State Object.
 * @returns
 */
async function addRouterPatches(state) {
    const { NavigationEnd, NavigationStart, Router } = await import('@angular/router');
    const { filter } = await import('rxjs/operators');
    const router = state.ngModuleRef.injector.get(Router, null);
    if (!router || routerPatchedModules.has(state.ngModuleRef)) {
        return;
    }
    const onNavigation = zoneWrapper(state, (event) => {
        if (event instanceof NavigationStart) {
            const currentNavigation = router.getCurrentNavigation();
            if (currentNavigation && currentNavigation.trigger !== 'imperative') {
                currentNavigation.extras.skipLocationChange = true;
                currentNavigation.extras.replaceUrl = false;
            }
        }
        if (state.componentInjector && event instanceof NavigationEnd) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const parent = state.componentInjector.get(MFEParentNode);
            if (parent) {
                // Used to communicate to the host app that a route change happen
                parent.dispatchEvent(new CustomEvent('mfe-route-change', { detail: event.urlAfterRedirects }));
            }
        }
    });
    const subscription = router.events
        .pipe(filter(event => event instanceof NavigationStart || event instanceof NavigationEnd))
        .subscribe(onNavigation);
    // We need to remove any subscription
    state.ngModuleRef.onDestroy(() => {
        subscription.unsubscribe();
    });
    routerPatchedModules.add(state.ngModuleRef);
}
/**
 * Executes a callback inside the Zone if there is one or directly into the current scope if there is none.
 * @param state - State to get the potential Zone.
 * @param fn - Function to execute.
 * @returns Result of fn.
 */
function zoneWrapper(state, fn) {
    return (...args) => {
        if (state.ngZone) {
            return state.ngZone.run(() => fn(...args));
        }
        return fn(...args);
    };
}
/**
 * Creates a MFE LifeCycle instance from an Angular Module and Platform.
 * @public
 * @param options - Creator options ({@link MFELoaderOptions}).
 * @returns Instance of LifeCycle that will be used to mount and control the MFE.
 */
export function ngLifecycle(options) {
    if (typeof options !== 'object') {
        /* C8 ignore next */
        throw Error('mfe-loader: requires a configuration object');
    }
    const state = {
        inputRecords: [],
        inputChanges: {},
    };
    return {
        bootstrap: bootstrap.bind(null, state, options),
        // All lifecycle (but bootstrap) hooks should be execute inside the MFE Zone
        mount: zoneWrapper(state, mount.bind(null, state, options)),
        unmount: zoneWrapper(state, unmount.bind(null, state)),
        update: zoneWrapper(state, update.bind(null, state)),
    };
}
/**
 * Creates a {@link AngularLifecycleFactory} for an Angular module.
 * @remarks This is to save boilerplate code.
 * @public
 * @param ngModule - Angular Module class.
 * @param useZone - Defines if a Zone will be used in the Angular tree.
 * @param shadow - Defines if a Shadow Root will be use to provide isolation to the MFE.
 * @returns Instance of {@link AngularLifecycleFactory}
 */
export function ngLifecycleFactory(ngModule, shadow, useZone = true) {
    return ({ ngPlatform } = {}) => (Object.assign(Object.assign({}, ngLifecycle({
        ngPlatform,
        ngModule,
        useZone,
    })), { shadow }));
}
/**
 * Creates a {@link AngularLifecycleFactory} for an Angular module that will not use Zone.js.
 * @remarks This will create Angular without a Zone.
 * @public
 * @param ngModule - Angular Module class.
 * @param shadow - Defines if a Shadow Root will be use to provide isolation to the MFE.
 * @returns Instance of {@link AngularLifecycleFactory}.
 */
export function ngZoneLessLifecycleFactory(ngModule, shadow) {
    return ngLifecycleFactory(ngModule, shadow, false);
}
